/* eslint-disable no-restricted-imports -- the one place dayjs and its
   plugins are imported directly */
/**
 * dayjs with the plugins the UI uses. It doesn't export dayjs itself: use
 * dayjsLondon or dayjsUtc, so every date says which timezone it's in rather
 * than taking the browser's.
 *
 * The locale is dayjs's default English, as moment's was here, so locale
 * formats ('LT' is 3:30 PM) and the date picker's calendar (weeks start on
 * Sunday) are unchanged.
 */
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import localeData from 'dayjs/plugin/localeData'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(advancedFormat)
dayjs.extend(customParseFormat)
dayjs.extend(duration)
dayjs.extend(isBetween)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(localeData)
dayjs.extend(localizedFormat)
dayjs.extend(relativeTime)
dayjs.extend(utc)
dayjs.extend(timezone)

/** Our staff and customers are in the UK, so dates they see are London time */
export const LONDON = 'Europe/London'

type DateInput = Parameters<typeof dayjs>[0]

/** Ends with Z or an offset like +01:00, so it says its own timezone */
const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i

/**
 * A date in UTC. Like dayjs(), with an optional format to parse by. A string
 * without an offset is read as a UTC time. Use it for elapsed time ("7 days
 * ago") and for dates with no time, such as dates of birth, which it keeps
 * exact.
 */
export const dayjsUtc = (date?: DateInput, format?: string, strict?: boolean) =>
  dayjs.utc(date, format, strict)

/**
 * A date in London time. Like dayjs(), with an optional format to parse by.
 * A string without an offset ('2024-07-01', '2024-07-01T09:30') is read as a
 * London time; anything else is the instant it names, shown in London.
 *
 * Use it to read, show and compare. Don't add or subtract on it: across a
 * clock change dayjs can get the hour wrong. Work out elapsed time with
 * dayjsUtc, and calendar dates with atLondonTime and addCalendarDays.
 */
export const dayjsLondon = (
  date?: DateInput,
  format?: string,
  strict?: boolean
) => {
  if (typeof date === 'string' && !HAS_OFFSET.test(date)) {
    if (strict && format && !dayjs(date, format, true).isValid()) {
      return dayjs(null)
    }
    return format ? dayjs.tz(date, format, LONDON) : dayjs.tz(date, LONDON)
  }
  const parsed = dayjs(date, format, strict)
  return parsed.isValid() ? parsed.tz(LONDON) : parsed
}

/**
 * The given London calendar date at the given time, as an instant. Build
 * times this way, rather than adding days to a London time: dayjs keeps a
 * time's UTC offset when adding days, so across a clock change the hour
 * would move.
 */
export const atLondonTime = (
  date: string,
  { hour, minute = 0 }: { hour: number; minute?: number }
) =>
  dayjsLondon(
    `${date} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
    'YYYY-MM-DD HH:mm'
  )

/** Adds days to a YYYY-MM-DD calendar date */
export const addCalendarDays = (date: string, days: number) =>
  dayjsUtc(date, 'YYYY-MM-DD').add(days, 'day').format('YYYY-MM-DD')

/** Today's date in London, as YYYY-MM-DD */
export const londonToday = () => dayjsLondon().format('YYYY-MM-DD')

/** A length of time: a number of milliseconds, or an ISO 8601 duration
 * such as 'P7D' */
export const durationOf = (value: number | string) =>
  typeof value === 'number' ? dayjs.duration(value) : dayjs.duration(value)

/** The current locale's first day of the week, 0 for Sunday */
export const firstDayOfWeek = () => dayjs.localeData().firstDayOfWeek()

export type { Dayjs, ManipulateType } from 'dayjs'
